import { Box, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GodAviForm as GodAviFormType, LithologyForm as LithologyFormType } from "../../../types/forms/capture";
import { GodAviResult } from "../../../types/interfaces/methods";
import { CaptureLithologyResponse } from "../../../types/model/captureLithology";
import CaptureLithologiesDataGrid from "../../Captures/Lithology/CaptureLithologiesDataGrid";
import GodAviForm from "./GodAviForm";

interface CaptureGodAviProps {
  captureId: number;
  canEdit: boolean;

  lithologiesData: CaptureLithologyResponse[];
  onCreateLithology: (values: LithologyFormType) => Promise<void>;
  onUpdateLithology: (lithologyId: number, values: LithologyFormType) => Promise<void>;
  onDeleteLithology: (lithologyId: number) => Promise<void>;

  onCreate: (values: GodAviFormType) => Promise<void>;
  godAviInputs?: GodAviFormType | null;
  godAviResult?: GodAviResult | null;

  onRequestRefresh?: () => void;
}

export default function CaptureGodAvi({
  captureId,
  canEdit,
  lithologiesData,
  onCreateLithology,
  onUpdateLithology,
  onDeleteLithology,
  onCreate,
  godAviInputs,
  godAviResult,
  onRequestRefresh
}: Readonly<CaptureGodAviProps>) {
  const { t } = useTranslation();

  return (
    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
      <Stack spacing={5}>
        <CaptureLithologiesDataGrid
          captureId={captureId}
          canEdit={canEdit}
          lithologiesData={lithologiesData}
          onCreate={async (values) => {
            await onCreateLithology(values);
          }}
          onUpdate={async (id, values) => {
            await onUpdateLithology(id, values);
          }}
          onDelete={async (id) => {
            await onDeleteLithology(id);
          }}
          onRequestRefresh={onRequestRefresh}
        />

        <GodAviForm
          onSubmit={async (values) => {
            await onCreate(values);
          }}
          canEdit={canEdit}
          godAviInputs={godAviInputs}
        />

        {godAviResult && (
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: 2,
            }}
          >
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                {t("calculations.vulnerability.god-avi.result.title")}
              </Typography>

              <Box
                sx={{
                  border: "2px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderBottomColor: "divider",
                    borderRight: "1px solid",
                    borderRightColor: "divider",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600}}>
                    {t(
                      "calculations.vulnerability.god-avi.result.god-index"
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>
                    {godAviResult?.results.godIndex.toFixed(2)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderBottomColor: "divider",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t(
                      "calculations.vulnerability.god-avi.result.god-classification"
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>
                    {godAviResult?.results.godClassification}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderBottomColor: "divider",
                    borderRight: "1px solid",
                    borderRightColor: "divider",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t(
                      "calculations.vulnerability.god-avi.result.avi-index"
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>
                    {godAviResult?.results?.aviIndex?.toFixed(2)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderBottomColor: "divider",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t(
                      "calculations.vulnerability.god-avi.result.avi-classification"
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>
                    {godAviResult?.results.aviClassification}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        )}
      </Stack>
    </Paper>
  );
}
